import React, { useRef } from 'react';
import { useCardsPerRow } from 'hooks/useCardsPerRow';
import { TraderListCollapseApi } from 'hooks/useTraderListCollapse';
import { COLLAPSED_ROWS } from 'utils/traderListCollapse';
import './TraderListCollapse.css';

const Chevron: React.FC<{ direction: 'up' | 'down' }> = ({ direction }) => (
  <svg
    className="trader-collapse-chevron"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
    style={direction === 'up' ? { transform: 'rotate(180deg)' } : undefined}
  >
    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Arrows pointing in (collapse) or out (expand), for the icon-only mobile toggle. */
const CompressIcon: React.FC<{ expand: boolean }> = ({ expand }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    {expand ? (
      <path d="M8 2v4M8 2L6 4M8 2l2 2M8 14v-4M8 14l-2-2M8 14l2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    ) : (
      <path d="M8 6V2M8 6L6 4M8 6l2-2M8 10v4M8 10l-2 2M8 10l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    )}
  </svg>
);

/** Toolbar toggle: "Collapse all" until it is on, then "Expand all". */
export const CollapseAllButton: React.FC<{
  collapseAll: boolean;
  onToggle: () => void;
  /** Icon-only where the row has no room for a label (Activity on a phone). */
  iconOnly?: boolean;
  className?: string;
}> = ({ collapseAll, onToggle, iconOnly = false, className }) => {
  const label = collapseAll ? 'Expand all' : 'Collapse all';
  return (
    <button
      type="button"
      className={`trader-collapse-all ${iconOnly ? 'trader-collapse-all--icon' : ''} ${className || ''}`}
      aria-label={label}
      aria-pressed={collapseAll}
      title={iconOnly ? label : undefined}
      onClick={onToggle}
      data-testid="trader-collapse-all"
    >
      {iconOnly ? <CompressIcon expand={collapseAll} /> : label}
    </button>
  );
};

/** Header control for one trader's list. */
export const TraderCollapseToggle: React.FC<{
  collapsed: boolean;
  onToggle: () => void;
  username?: string;
  className?: string;
}> = ({ collapsed, onToggle, username, className }) => {
  const label = collapsed ? 'Expand' : 'Collapse';
  return (
    <button
      type="button"
      className={`trader-collapse-toggle ${className || ''}`}
      aria-expanded={!collapsed}
      aria-label={username ? `${label} ${username}'s list` : label}
      onClick={onToggle}
      data-testid="trader-collapse-toggle"
    >
      {label}
      <Chevron direction={collapsed ? 'down' : 'up'} />
    </button>
  );
};

const formatCount = (n: number) => new Intl.NumberFormat('en-US').format(n);

/** Under a collapsed list: expands that trader. */
export const ShowAllButton: React.FC<{
  total: number;
  shown: number;
  noun: 'cards' | 'wants';
  onClick: () => void;
}> = ({ total, shown, noun, onClick }) => (
  <button type="button" className="trader-collapse-show-all" onClick={onClick} data-testid="trader-collapse-show-all">
    <span>
      Show all {formatCount(total)}
      <span className="trader-collapse-show-all__noun"> {noun}</span>
    </span>
    <span className="trader-collapse-show-all__more">+{formatCount(Math.max(total - shown, 0))} more</span>
    <Chevron direction="down" />
  </button>
);

/** Under an expanded long list, so nobody scrolls 2,000 cards back up to the header. */
export const CollapseListButton: React.FC<{ username: string; onClick: () => void }> = ({ username, onClick }) => (
  <button type="button" className="trader-collapse-bottom" onClick={onClick} data-testid="trader-collapse-bottom">
    Collapse {username}'s list
    <Chevron direction="up" />
  </button>
);

/** Top-left of a card image, collapsed view only. */
export const NewWantTag: React.FC = () => (
  <span className="trader-collapse-new-tag" data-testid="trader-collapse-new-tag">
    NEW
  </span>
);

export interface TraderCollapseContext {
  /** Attach to the element whose direct children are the tiles (image layouts). */
  gridRef: React.RefObject<HTMLDivElement | null>;
  /** The list is long and the saved setting says collapsed. */
  collapsed: boolean;
  /** The list exceeds the collapsed size, so it gets controls at all. */
  isLong: boolean;
  /** The first N tiles when collapsed, everything otherwise. */
  limit: <T>(tiles: T[]) => T[];
  /** Per-trader Collapse / Expand button, or null for a list that already fits. */
  headerToggle: React.ReactNode;
  /** "Show all" under a collapsed list, "Collapse {username}'s list" under an expanded long one. */
  footer: React.ReactNode;
}

interface TraderCollapseProps {
  traderId: number | string;
  username: string;
  /** Tiles (or rows) the full list would render in the current layout. */
  total: number;
  noun: 'cards' | 'wants';
  collapse: TraderListCollapseApi;
  /** False where collapsing does not apply (e.g. an empty auto-match state). */
  enabled?: boolean;
  /**
   * A fixed collapsed size (Send's List density: 5 rows). Omit for the image
   * layouts, where the size is two measured rows.
   */
  fixedLimit?: number;
  /** Anything else that changes the tile layout (density, tile size). */
  layoutKey?: string;
  children: (context: TraderCollapseContext) => React.ReactNode;
}

/**
 * One trader's collapsible list. Owns the measurement and the saved state and
 * hands the caller what to render; the caller keeps its own tiles and markup.
 * Toggling is a render-only change plus one preference write.
 */
export const TraderCollapse: React.FC<TraderCollapseProps> = ({
  traderId,
  username,
  total,
  noun,
  collapse,
  enabled = true,
  fixedLimit,
  layoutKey,
  children,
}) => {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const wantsCollapsed = collapse.isCollapsed(traderId);
  const perRow = useCardsPerRow(gridRef, `${total}|${wantsCollapsed}|${layoutKey ?? ''}`);

  const collapsedSize =
    fixedLimit !== undefined ? fixedLimit : perRow === null ? Infinity : perRow * COLLAPSED_ROWS;
  const isLong = enabled && total > collapsedSize;
  const collapsed = isLong && wantsCollapsed;
  const shown = collapsed ? collapsedSize : total;

  const context: TraderCollapseContext = {
    gridRef,
    collapsed,
    isLong,
    limit: (tiles) => (collapsed ? tiles.slice(0, collapsedSize) : tiles),
    headerToggle: isLong ? (
      <TraderCollapseToggle
        collapsed={collapsed}
        username={username}
        onToggle={() => collapse.setTraderCollapsed(traderId, !collapsed)}
      />
    ) : null,
    footer: !isLong ? null : collapsed ? (
      <ShowAllButton
        total={total}
        shown={shown}
        noun={noun}
        onClick={() => collapse.setTraderCollapsed(traderId, false)}
      />
    ) : (
      <CollapseListButton username={username} onClick={() => collapse.setTraderCollapsed(traderId, true)} />
    ),
  };

  return <>{children(context)}</>;
};

export default TraderCollapse;
